import { useState } from "react";
import { Clock, Lock, Sparkles, ArrowDown, FolderOpen } from "lucide-react";
import { SavedSimulationsModal } from "../shared/SavedSimulationsModal";
import { Logo } from "../shared/Logo";

interface HeroProps {
  onCTA: () => void;
}

export function Hero({ onCTA }: HeroProps) {
  const [savedOpen, setSavedOpen] = useState(false);
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-primary-soft via-background to-background">
      {/* Decorative blobs */}
      <div className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 h-96 w-96 rounded-full bg-success/10 blur-3xl" />

      <div className="relative mx-auto max-w-6xl px-6 pb-16 pt-10 lg:px-12 lg:pt-14">
        {/* Header avec logo */}
        <div className="mb-10 flex items-center justify-between">
          <Logo size={44} />
          <span className="hidden text-xs font-medium text-muted-foreground sm:inline">
            Simulateur LMNP — édition 2026
          </span>
        </div>

        <div className="grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <h1 className="mt-5 text-4xl font-bold leading-tight tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              Votre LMNP est-il{" "}
              <span className="bg-gradient-to-r from-primary to-primary-dark bg-clip-text text-transparent">
                vraiment rentable
              </span>{" "}
              ?
            </h1>
            <p className="mt-5 max-w-xl text-lg text-muted-foreground">
              Calcul gratuit en 2 minutes. Cash-flow, rendement net, score d'investissement —
              sans jargon, sans création de compte.
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
              <Badge icon={<Clock className="h-3.5 w-3.5" />}>2 minutes</Badge>
              <Badge icon={<Lock className="h-3.5 w-3.5" />}>Aucune donnée sauvegardée</Badge>
              <Badge icon={<Sparkles className="h-3.5 w-3.5" />}>100% gratuit</Badge>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                onClick={onCTA}
                className="group inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3.5 text-base font-semibold text-primary-foreground shadow-lg shadow-primary/30 transition-all hover:-translate-y-0.5 hover:bg-primary-dark hover:shadow-xl hover:shadow-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2"
              >
                Calculer ma rentabilité
                <ArrowDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
              </button>
              <button
                type="button"
                onClick={() => setSavedOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-background px-4 py-3 text-sm font-medium text-muted-foreground shadow-sm transition hover:-translate-y-0.5 hover:border-primary/40 hover:text-foreground"
              >
                <FolderOpen className="h-4 w-4" />
                Mes simulations
              </button>
            </div>
            <SavedSimulationsModal open={savedOpen} onOpenChange={setSavedOpen} />
          </div>

          {/* Building illustration */}
          <div className="hidden lg:col-span-5 lg:block">
            <BuildingIllustration />
          </div>
        </div>
      </div>
    </section>
  );
}

function Badge({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground shadow-sm">
      {icon}
      {children}
    </span>
  );
}

function BuildingIllustration() {
  return (
    <svg viewBox="0 0 400 400" className="h-auto w-full max-w-md">
      {/* sky/ground */}
      <defs>
        <linearGradient id="bldg" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="oklch(0.546 0.215 262)" />
          <stop offset="100%" stopColor="oklch(0.485 0.22 263)" />
        </linearGradient>
      </defs>
      <rect x="0" y="320" width="400" height="80" fill="oklch(0.96 0.025 250)" />
      {/* main building */}
      <rect x="120" y="80" width="160" height="240" rx="6" fill="url(#bldg)" />
      {/* roof */}
      <rect x="110" y="70" width="180" height="14" rx="4" fill="oklch(0.485 0.22 263)" />
      {/* windows grid */}
      {Array.from({ length: 6 }).map((_, row) =>
        Array.from({ length: 4 }).map((_, col) => (
          <rect
            key={`${row}-${col}`}
            x={140 + col * 30}
            y={100 + row * 36}
            width="20"
            height="22"
            rx="2"
            fill={(row + col) % 3 === 0 ? "oklch(0.95 0.06 90)" : "oklch(0.99 0.005 250)"}
            opacity={0.95}
          />
        ))
      )}
      {/* door */}
      <rect x="185" y="280" width="30" height="40" rx="3" fill="oklch(0.205 0.04 256)" />
      {/* small left building */}
      <rect x="40" y="160" width="70" height="160" rx="4" fill="oklch(0.708 0.155 163)" opacity="0.9" />
      {Array.from({ length: 4 }).map((_, row) =>
        Array.from({ length: 2 }).map((_, col) => (
          <rect
            key={`l-${row}-${col}`}
            x={52 + col * 26}
            y={176 + row * 32}
            width="16"
            height="18"
            rx="2"
            fill="oklch(0.99 0.005 250)"
            opacity={0.9}
          />
        ))
      )}
      {/* small right building */}
      <rect x="290" y="190" width="70" height="130" rx="4" fill="oklch(0.778 0.165 70)" opacity="0.9" />
      {Array.from({ length: 3 }).map((_, row) =>
        Array.from({ length: 2 }).map((_, col) => (
          <rect
            key={`r-${row}-${col}`}
            x={302 + col * 26}
            y={206 + row * 32}
            width="16"
            height="18"
            rx="2"
            fill="oklch(0.99 0.005 250)"
            opacity={0.9}
          />
        ))
      )}
      {/* € floating */}
      <g>
        <circle cx="330" cy="110" r="28" fill="oklch(0.99 0.005 250)" stroke="oklch(0.546 0.215 262)" strokeWidth="2" />
        <text x="330" y="120" textAnchor="middle" fontSize="28" fontWeight="800" fill="oklch(0.546 0.215 262)">€</text>
      </g>
    </svg>
  );
}
